import React, { useState } from 'react';
import {
  Button,
  Form,
  Input,
  InputNumber,
  Modal,
  Popconfirm,
  Space,
  Table,
  Typography,
  message,
} from 'antd';
import { PlusOutlined } from '@ant-design/icons';
import {
  useCreateData,
  useDeleteData,
  useFetchData,
  useUpdateData,
} from '../../../hooks';
import { useAuthStore } from '../../../store';

// Admin / Configuration — Number Series (Sequencer). Custom form because the
// generic code+description form can't express prefix/padding/next-number/etc.
interface Sequence {
  id?: string;
  code: string;
  description?: string;
  prefix?: string;
  suffix?: string;
  padding?: number;
  next_number?: number;
  increment?: number;
}

const API = '/sequences';

const NumberSeriesPage = () => {
  const { user } = useAuthStore();
  const isAdmin = user?.role === 'ROLE_ADMIN';
  const { data, isLoading } = useFetchData<{ data: Sequence[] }>(API, {
    length: 999999,
  });
  const create = useCreateData<Sequence>(API);
  const update = useUpdateData<Sequence>(API);
  const del = useDeleteData<Sequence>(API);

  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState<Sequence | null>(null);
  const [form] = Form.useForm();

  const openCreate = () => {
    setEditing(null);
    form.resetFields();
    form.setFieldsValue({ padding: 5, increment: 1, next_number: 1 });
    setOpen(true);
  };
  const openEdit = (rec: Sequence) => {
    setEditing(rec);
    form.setFieldsValue(rec);
    setOpen(true);
  };

  const onSubmit = async () => {
    const values = await form.validateFields();
    const onSuccess = () => {
      message.success(editing ? 'Series updated' : 'Series created');
      setOpen(false);
    };
    const onError = (e: any) =>
      message.error(e?.response?.data?.message ?? 'Save failed');
    if (editing?.id) {
      update.mutate(
        { id: editing.id, form: { ...editing, ...values } },
        { onSuccess, onError },
      );
    } else {
      create.mutate(values, { onSuccess, onError });
    }
  };

  const columns: any[] = [
    { title: 'Code', dataIndex: 'code' },
    { title: 'Description', dataIndex: 'description' },
    { title: 'Prefix', dataIndex: 'prefix' },
    { title: 'Suffix', dataIndex: 'suffix' },
    { title: 'Padding', dataIndex: 'padding' },
    { title: 'Next #', dataIndex: 'next_number' },
    { title: 'Increment', dataIndex: 'increment' },
  ];
  if (isAdmin) {
    columns.push({
      title: 'Actions',
      width: 160,
      render: (_: unknown, rec: Sequence) => (
        <Space>
          <Button size={'small'} onClick={() => openEdit(rec)}>
            Edit
          </Button>
          <Popconfirm
            title={'Delete this series?'}
            onConfirm={() =>
              del.mutate(
                { id: rec.id! },
                { onSuccess: () => message.success('Series deleted') },
              )
            }
          >
            <Button size={'small'} danger>
              Delete
            </Button>
          </Popconfirm>
        </Space>
      ),
    });
  }

  return (
    <div className={'page-wrapper'}>
      <div className={'flex items-center justify-between mb-3'}>
        <Typography.Title level={4} style={{ margin: 0 }}>
          Number Series
        </Typography.Title>
        {isAdmin && (
          <Button type={'primary'} icon={<PlusOutlined />} onClick={openCreate}>
            New Series
          </Button>
        )}
      </div>
      <Table
        rowKey={(r) => r.id || r.code}
        loading={isLoading}
        dataSource={data?.data || []}
        columns={columns}
        pagination={{ pageSize: 20, showSizeChanger: true }}
      />
      <Modal
        title={editing ? 'Edit Series' : 'New Series'}
        open={open}
        onOk={onSubmit}
        onCancel={() => setOpen(false)}
        confirmLoading={create.isPending || update.isPending}
        destroyOnClose
      >
        <Form form={form} layout={'vertical'}>
          <Form.Item
            name={'code'}
            label={'Code'}
            rules={[{ required: true, message: 'Code is required' }]}
          >
            <Input disabled={!!editing} placeholder={'e.g. PRDO'} />
          </Form.Item>
          <Form.Item name={'description'} label={'Description'}>
            <Input />
          </Form.Item>
          <Form.Item name={'prefix'} label={'Prefix'}>
            <Input placeholder={'e.g. PRDO-'} />
          </Form.Item>
          <Form.Item name={'suffix'} label={'Suffix'}>
            <Input />
          </Form.Item>
          <Form.Item name={'padding'} label={'Padding (digits)'}>
            <InputNumber min={0} max={12} style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item name={'next_number'} label={'Next Number'}>
            <InputNumber min={0} style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item name={'increment'} label={'Increment'}>
            <InputNumber min={1} style={{ width: '100%' }} />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default NumberSeriesPage;
